import React from "react";
import { useNavigate } from "react-router-dom";
import { DollarSign, MapPin, User } from "lucide-react";
import Deal from "@/model/interface/Deal";
import { getContactDisplayName } from "@/util/string";
import { memberName } from "@/util/member";
import ContactStatePill from "@/components/contacts/ContactStatePill";

interface DealKanbanCardProps {
  deal: Deal;
}

export function DealKanbanCard({ deal }: DealKanbanCardProps) {
  const navigate = useNavigate();

  const handleDragStart = (e: React.DragEvent) => {
    e.dataTransfer.setData("dealId", deal.id);
  };

  const contactName = getContactDisplayName(deal.contact);

  const formatCurrency = (value?: number) => {
    if (value == null) return null;
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  return (
    <div
      draggable
      onDragStart={handleDragStart}
      onClick={() => navigate(`/deals/${deal.id}`)}
      className="bg-background rounded-md border p-3 cursor-pointer hover:shadow-md transition-shadow space-y-2"
    >
      {deal.propertyAddress && (
        <div className="flex items-start gap-1.5">
          <MapPin className="h-3.5 w-3.5 text-muted-foreground mt-0.5 shrink-0" />
          <span className="text-sm font-medium leading-tight">{deal.propertyAddress}</span>
        </div>
      )}
      {deal.propertyCity && (
        <p className="text-xs text-muted-foreground ml-5">
          {deal.propertyCity}{deal.propertyState ? `, ${deal.propertyState}` : ""} {deal.propertyZip || ""}
        </p>
      )}
      {!deal.propertyAddress && contactName && (
        <span className="inline-flex items-center gap-2 text-sm font-medium">
          {contactName}
          <ContactStatePill contact={deal.contact} />
        </span>
      )}
      <div className="flex items-center justify-between">
        {contactName && deal.propertyAddress && (
          <div className="flex items-center gap-1 text-xs text-muted-foreground">
            <User className="h-3 w-3" />
            <span>{contactName}</span>
            <ContactStatePill contact={deal.contact} />
          </div>
        )}
        {deal.offerPrice != null && (
          <div className="flex items-center gap-0.5 text-xs font-medium text-green-600">
            <DollarSign className="h-3 w-3" />
            <span>{formatCurrency(deal.offerPrice)}</span>
          </div>
        )}
      </div>
      {deal.assignedMembership && (
        <div className="text-xs text-muted-foreground">
          {memberName(deal.assignedMembership)}
        </div>
      )}
    </div>
  );
}
